import React, { useState } from 'react';
import { Calculator, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { mockQuoteRequests, mockServices } from '../mock';

export const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    propertyType: '',
    propertySize: '',
    services: [],
    frequency: '',
    specialRequirements: '',
    preferredContactTime: '',
    urgency: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(null);

  const propertyTypes = [
    'Residential Home',
    'Residential Estate',
    'Small Office (1-20 employees)',
    'Medium Office (21-100 employees)',
    'Large Office (100+ employees)',
    'Retail Store',
    'Shopping Center',
    'Industrial Facility',
    'Warehouse',
    'Event Venue',
    'Other'
  ];

  const frequencies = [
    'One-time service',
    'Weekly',
    'Bi-weekly',
    'Monthly',
    'Quarterly',
    'As needed'
  ];

  const contactTimes = [
    'Morning (8AM - 12PM)',
    'Afternoon (12PM - 5PM)',
    'Evening (5PM - 8PM)',
    'Anytime'
  ];

  const urgencyLevels = [
    'Not urgent - within 2 weeks',
    'Soon - within 1 week',
    'Urgent - within 3 days',
    'Emergency - immediate'
  ];

  const allServices = [...mockServices.cleaning, ...mockServices.security];

  const handleServiceChange = (serviceId, checked) => {
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, serviceId]
        : prev.services.filter(id => id !== serviceId)
    }));
  };

  const calculateEstimate = () => {
    // Simple pricing logic for mock data
    let baseCost = 0;
    const selectedServices = allServices.filter(service => 
      formData.services.includes(service.id)
    );
    
    selectedServices.forEach(service => {
      if (service.id <= 3) baseCost += 500; // Cleaning services
      else baseCost += 800; // Security services
    });

    // Property size multiplier
    const sizeMultiplier = {
      'Small (< 100 sqm)': 0.8,
      'Medium (100-500 sqm)': 1.0,
      'Large (500-1000 sqm)': 1.5,
      'Extra Large (> 1000 sqm)': 2.0
    }[formData.propertySize] || 1.0;

    // Frequency discount
    const frequencyMultiplier = {
      'One-time service': 1.0,
      'Weekly': 0.8,
      'Bi-weekly': 0.85,
      'Monthly': 0.9,
      'Quarterly': 0.95,
      'As needed': 1.0
    }[formData.frequency] || 1.0;

    const estimate = Math.round(baseCost * sizeMultiplier * frequencyMultiplier);
    setEstimatedCost(estimate);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Calculate estimate
    calculateEstimate();
    
    // Mock submission
    const newQuote = {
      id: Date.now(),
      ...formData,
      timestamp: new Date(),
      status: 'pending'
    };
    mockQuoteRequests.push(newQuote);
    
    setSubmitted(true);
    
    // In real app, this would send to backend
    console.log('Quote request submitted:', newQuote);
  };

  if (submitted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="text-center py-12 space-y-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">Quote Request Received!</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Thank you for your interest in La-Klin services. We'll contact you within 24 hours 
            with a detailed quote tailored to your needs.
          </p>
          {estimatedCost && (
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-sm text-gray-600 mb-2">Estimated Monthly Cost:</p>
              <p className="text-3xl font-bold text-red-600">R{estimatedCost.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-2">
                *Final quote may vary based on specific requirements
              </p>
            </div>
          )}
          <Button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '', email: '', phone: '', company: '', propertyType: '',
                propertySize: '', services: [], frequency: '', specialRequirements: '',
                preferredContactTime: '', urgency: ''
              });
              setEstimatedCost(null);
            }}
            variant="outline"
          >
            Submit Another Quote
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-2xl">
          <Calculator className="w-6 h-6 text-red-600" />
          <span>Get Your Custom Quote</span>
        </CardTitle>
        <p className="text-gray-600">
          Tell us about your needs and we'll provide a tailored quote for your security and cleaning requirements.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="Your full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                placeholder="your@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
                placeholder="+27 XX XXX XXXX"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company/Organization</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Property Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Property Type *</Label>
              <Select onValueChange={(value) => setFormData(prev => ({ ...prev, propertyType: value }))} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Property Size *</Label>
              <Select onValueChange={(value) => setFormData(prev => ({ ...prev, propertySize: value }))} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select size range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Small (< 100 sqm)">Small (&lt; 100 sqm)</SelectItem>
                  <SelectItem value="Medium (100-500 sqm)">Medium (100-500 sqm)</SelectItem>
                  <SelectItem value="Large (500-1000 sqm)">Large (500-1000 sqm)</SelectItem>
                  <SelectItem value="Extra Large (> 1000 sqm)">Extra Large (&gt; 1000 sqm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Services Selection */}
          <div className="space-y-4">
            <Label>Services Required * (Select all that apply)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allServices.map(service => (
                <div key={service.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`service-${service.id}`}
                    checked={formData.services.includes(service.id)}
                    onCheckedChange={(checked) => handleServiceChange(service.id, checked)}
                  />
                  <Label htmlFor={`service-${service.id}`} className="text-sm">
                    {service.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Service Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Service Frequency *</Label>
              <Select onValueChange={(value) => setFormData(prev => ({ ...prev, frequency: value }))} required>
                <SelectTrigger>
                  <SelectValue placeholder="How often?" />
                </SelectTrigger>
                <SelectContent>
                  {frequencies.map(freq => (
                    <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Preferred Contact Time</Label>
              <Select onValueChange={(value) => setFormData(prev => ({ ...prev, preferredContactTime: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="When to call you?" />
                </SelectTrigger>
                <SelectContent>
                  {contactTimes.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Timeline/Urgency</Label>
            <Select onValueChange={(value) => setFormData(prev => ({ ...prev, urgency: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="When do you need this?" />
              </SelectTrigger>
              <SelectContent>
                {urgencyLevels.map(level => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Special Requirements */}
          <div className="space-y-2">
            <Label htmlFor="requirements">Special Requirements or Notes</Label>
            <Textarea
              id="requirements"
              value={formData.specialRequirements}
              onChange={(e) => setFormData(prev => ({ ...prev, specialRequirements: e.target.value }))}
              placeholder="Any specific requirements, preferences, or additional information..."
              rows={4}
            />
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3 text-lg font-semibold"
            disabled={!formData.name || !formData.email || !formData.phone || !formData.propertyType || !formData.propertySize || formData.services.length === 0 || !formData.frequency}
          >
            <Send className="w-5 h-5 mr-2" />
            Get My Quote
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};